import React from 'react';
import { Avatar, Badge, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { roleColors } from '../../styles/theme';
import { UserRole } from '../../types';

interface UserAvatarProps {
  user: {
    name: string;
    profilePicture?: string;
    role?: UserRole;
  };
  size?: 'small' | 'medium' | 'large';
  showRole?: boolean;
  onClick?: () => void;
  className?: string;
}

const StyledBadge = styled(Badge, {
  shouldForwardProp: (prop) => prop !== 'userRole',
})<{ userRole?: UserRole }>(({ theme, userRole }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: userRole ? roleColors[userRole].text : theme.palette.primary.main,
    color: '#FFFFFF',
    fontSize: '0.75rem',
    fontWeight: 600,
    minWidth: '20px',
    height: '20px',
    borderRadius: '10px',
    border: `2px solid ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      content: '""',
    },
  },
}));

const getAvatarSize = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return { width: 32, height: 32 };
    case 'medium':
      return { width: 40, height: 40 };
    case 'large':
      return { width: 56, height: 56 };
    default:
      return { width: 40, height: 40 };
  }
};

const getUserInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
};

const getRoleBadge = (role: UserRole): string => {
  switch (role) {
    case 'Faculty':
      return 'F';
    case 'Ambassador':
      return 'A';
    case 'Student':
    default:
      return 'S';
  }
};

const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = 'medium',
  showRole = false,
  onClick,
  className,
}) => {
  const avatarSize = getAvatarSize(size);
  const initials = getUserInitials(user.name);

  const avatar = (
    <Avatar
      src={user.profilePicture}
      alt={user.name}
      sx={{
        ...avatarSize,
        cursor: onClick ? 'pointer' : 'default',
        fontSize: size === 'small' ? '0.875rem' : '1rem',
        fontWeight: 600,
        backgroundColor: user.role ? roleColors[user.role].background : undefined,
        color: user.role ? roleColors[user.role].text : undefined,
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'scale(1.05)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        } : {},
      }}
      onClick={onClick}
      className={className}
    >
      {user.profilePicture ? null : initials}
    </Avatar>
  );

  if (showRole && user.role) {
    return (
      <Tooltip title={`${user.name} (${user.role})`} arrow>
        <StyledBadge
          badgeContent={getRoleBadge(user.role)}
          userRole={user.role}
          overlap="circular"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          {avatar}
        </StyledBadge>
      </Tooltip>
    );
  }

  return onClick ? (
    <Tooltip title={user.name} arrow>
      {avatar}
    </Tooltip>
  ) : (
    avatar
  );
};

export default UserAvatar;